import request from '@/utils/request';

/** 分页查询当前用户通知 GET /liyuan-social/notifications/page
 *  行内除 targetId/targetUrl 外，成交类可带 marketId / collId / slug（雪花 ID 字符串）
 */
export function getNotificationPage(params = {}) {
  const query = {
    pageNum: params.pageNum ?? 1,
    pageSize: params.pageSize ?? 20,
  };
  if (params.type) query.type = params.type;
  return request.get('/liyuan-social/notifications/page', query);
}

/** 各类型未读数 GET /liyuan-social/notifications/unread-count
 *  返回 data: { followCount, generalCount, mentionCount, messageCount, totalCount }
 */
export function getNotificationUnreadCounts() {
  return request.get('/liyuan-social/notifications/unread-count');
}

/** 全部标记已读 PUT /liyuan-social/notifications/read-all
 *  @param {string} [type] GENERAL | FOLLOW | MESSAGE | MENTION；不传则全部类型
 */
export function markAllNotificationsRead(type) {
  const params = {};
  if (type) params.type = type;
  return request.put('/liyuan-social/notifications/read-all', null, { params });
}

/** 单条标记已读 PUT /liyuan-social/notifications/{id}/read */
export function markNotificationRead(id) {
  const nid = String(id ?? '').trim();
  if (!nid) return Promise.reject(new Error('missing notification id'));
  return request.put(`/liyuan-social/notifications/${encodeURIComponent(nid)}/read`, {});
}

/**
 * 我的评论列表 GET /liyuan-social/comments/my
 * @param {Object} [params]
 * @param {number} [params.pageNum=1]
 * @param {number} [params.pageSize=10]
 */
export function getMyComments(params = {}) {
  return request.get('/liyuan-social/comments/my', {
    pageNum: params.pageNum ?? 1,
    pageSize: params.pageSize ?? 10,
  });
}

/**
 * 分页查询评论 GET /liyuan-social/comments/page
 * @param {Object} params
 * @param {number|string} params.marketId 市场 ID（必填）
 * @param {number} [params.pageNum=1]
 * @param {number} [params.pageSize=10]
 */
export function getCommentPage(params = {}) {
  return request.get('/liyuan-social/comments/page', {
    marketId: params.marketId,
    pageNum: params.pageNum ?? 1,
    pageSize: params.pageSize ?? 10,
  });
}

/**
 * 一级评论的回复列表 GET /liyuan-social/comments/replies
 * @param {Object} params
 * @param {number|string} params.rootCommentId 一级评论 ID（必填）
 * @param {number} [params.pageNum=1]
 * @param {number} [params.pageSize=10]
 */
export function getCommentReplies(params = {}) {
  return request.get('/liyuan-social/comments/replies', {
    rootCommentId: params.rootCommentId,
    pageNum: params.pageNum ?? 1,
    pageSize: params.pageSize ?? 10,
  });
}

/** 当前用户关注列表，返回 { rows, total }；UID为字符串。 */
export function getFollowingPage(params = {}) {
  return request.get('/liyuan-social/follows/following', {
    pageNum: params.pageNum ?? 1,
    pageSize: params.pageSize ?? 20,
  });
}

/**
 * 关注用户 POST /liyuan-social/follows/{userId}
 * @param {number|string} userId
 */
export function followUser(userId) {
  return request.post(`/liyuan-social/follows/${String(userId)}`);
}

/**
 * 取消关注 DELETE /liyuan-social/follows/{userId}
 * @param {number|string} userId 被取消关注的用户 ID（path）
 */
export function unfollowUser(userId) {
  return request.delete(`/liyuan-social/follows/${String(userId)}`);
}

/**
 * 某用户的关注数 / 粉丝数 GET /liyuan-social/follows/{userId}/stats
 * @param {number|string} userId 目标用户 ID（path）
 * @returns {Promise<{ followersCount: number, followingCount: number }>}
 */
export function getFollowStats(userId) {
  return request.get(`/liyuan-social/follows/${String(userId)}/stats`);
}

/**
 * 查询与目标用户的关注状态 GET /liyuan-social/follows/{userId}/status
 * @param {number|string} userId 目标用户 ID（path）
 * @returns {Promise<{ followedByTarget: boolean, following: boolean, mutual: boolean }>}
 */
export function getFollowStatus(userId) {
  return request.get(`/liyuan-social/follows/${String(userId)}/status`);
}

/**
 * 新增评论 POST /liyuan-social/comments
 * @param {Object} params
 * @param {string} params.content 评论内容（≤400 字）
 * @param {string} [params.ctype] COMMENT | MARKET
 * @param {number|string} [params.targetId] 被评论的目标 ID
 */
export function addComment(params = {}) {
  const body = { content: String(params.content ?? '').trim() };
  if (params.ctype) body.ctype = params.ctype;
  if (params.targetId != null) body.targetId = String(params.targetId);
  return request.post('/liyuan-social/comments', body);
}

/** 首页新闻列表（已发布，固定 5 条）GET /liyuan-social/news/home/list */
export function getHomeNewsList() {
  return request.get('/liyuan-social/news/home');
}

/**
 * 查询新闻详情 GET /liyuan-social/news/{id}
 * @param {number|string} id 新闻 ID
 */
export function getNewsById(id) {
  const newsId = String(id ?? '').trim();
  if (!newsId) return Promise.reject(new Error('news id required'));
  return request.get(`/liyuan-social/news/${encodeURIComponent(newsId)}`);
}

/** 首页热门话题列表（启用状态，固定 5 条）GET /liyuan-social/topic/hot */
export function getHomeHotTopics() {
  return request.get('/liyuan-social/topic/hot');
}

/**
 * 查询话题详情 GET /liyuan-social/topic/{id}
 * @param {number|string} id 话题 ID
 */
export function getTopicById(id) {
  const topicId = String(id ?? '').trim();
  if (!topicId) return Promise.reject(new Error('topic id required'));
  return request.get(`/liyuan-social/topic/${encodeURIComponent(topicId)}`);
}

/** 常见问题列表（已启用，按序号升序）GET /liyuan-social/faq/list */
export function getFaqList() {
  return request.get('/liyuan-social/faq/list');
}

/** 市场规则列表 GET /liyuan-social/market-rule（TableDataInfo«市场规则»，rows[].content） */
export function getMarketRule(params) {
  return request.get('/liyuan-social/market-rule', { params });
}
